/**
 * Generate a picture — after looking for one that already exists.
 *
 * Generating costs the school money on every press and takes a minute, and the
 * thing a teacher asks for is very often already in the library: a school that
 * has taught "farm animals" for three years has a cow. So the flow deliberately
 * does not put a Generate button under the description box. Typing a description
 * searches the library first and shows what it found; generating is the second
 * step, taken knowingly, past what is already there.
 *
 * This is the rare case where an extra click is the feature. The alternative -
 * one press, straight to the provider - quietly bills the school for a fifth
 * copy of a cow.
 */

import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { Image, ImageStyle } from '../../lib/api/client'
import { api } from '../../lib/api/client'
import { useAiStatus } from '../../lib/useAiStatus'
import { toast } from '../../components/toast'
import { Button, Input, Spinner } from '../../components/ui'
import { Picture } from '../../components/Picture'

/** The sparkle used for every AI control, so it keeps meaning one thing. */
function Sparkle({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.9 5.6a4 4 0 0 0 2.5 2.5L22 12l-5.6 1.9a4 4 0 0 0-2.5 2.5L12 22l-1.9-5.6a4 4 0 0 0-2.5-2.5L2 12l5.6-1.9a4 4 0 0 0 2.5-2.5L12 2z" />
      <path d="M19 2.5l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" opacity=".85" />
    </svg>
  )
}

/** Words worth matching on. Mirrors the noise list in app/ai/tagging.py so the
 *  library search and the tags a generated picture receives agree. */
const NOISE = new Set([
  'a', 'an', 'the', 'of', 'in', 'on', 'at', 'for', 'with', 'and', 'or', 'to',
  'simple', 'picture', 'image', 'drawing', 'outline', 'black', 'white', 'big',
  'small', 'one', 'two', 'some', 'please', 'make', 'create', 'draw',
])

/**
 * Black outlines used to be the only possibility: the server put "no colour" in
 * front of every description, so "a colourful parrot" came back in black and
 * white with nothing on screen to say why. The choice is offered here rather than
 * left entirely to word-matching, because a teacher who has just been given the
 * wrong kind of picture needs a control, not a better guess.
 */
const STYLES: { value: ImageStyle; label: string; hint: string }[] = [
  {
    value: 'auto',
    label: 'Decide for me',
    hint: 'Outlines, unless you asked for colour',
  },
  {
    value: 'line_art',
    label: 'Black outlines',
    hint: 'For colouring in, and for black-and-white printing',
  },
  {
    value: 'colour',
    label: 'Colour cartoon',
    hint: 'Bright, flat colours that still print clearly',
  },
]

function keywords(text: string): string[] {
  const found: string[] = text.toLowerCase().match(/[a-z]+/g) ?? []
  return found.filter((word) => word.length > 2 && !NOISE.has(word))
}

/** Library pictures that plausibly answer the description already. */
export function matchesFor(library: Image[], description: string): Image[] {
  const words = keywords(description)
  if (words.length === 0) return []
  return library
    .map((image) => {
      const haystack = [image.original_filename, ...(image.tags ?? [])].join(' ').toLowerCase()
      const score = words.reduce((total, word) => (haystack.includes(word) ? total + 1 : total), 0)
      return { image, score }
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8)
    .map((row) => row.image)
}

export function PictureGenerator({
  library,
  onUse,
  onSaved,
}: {
  library: Image[]
  /** Called when a picture is chosen - an existing one, or a newly saved one. */
  onUse?: (imageId: string) => void
  /** Called after a generated picture is added to the library. */
  onSaved?: () => void
}) {
  const status = useAiStatus()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [searched, setSearched] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [style, setStyle] = useState<ImageStyle>('auto')
  const [candidate, setCandidate] = useState<
    { dataUrl: string; prompt: string; eventId: string; style: string } | null
  >(null)

  const matches = useMemo(
    () => (searched ? matchesFor(library, description) : []),
    [library, description, searched],
  )

  if (!status.images_available) return null

  const reset = () => {
    setDescription('')
    setSearched(false)
    setCandidate(null)
    setError(null)
    setStyle('auto')
  }

  const search = () => {
    if (!description.trim()) return
    setCandidate(null)
    setError(null)
    setSearched(true)
  }

  const generate = async () => {
    if (!description.trim()) return
    setIsGenerating(true)
    setCandidate(null)
    setError(null)
    try {
      const result = await api.generateImage(description, style)
      setCandidate({
        dataUrl: result.data_url,
        prompt: result.prompt,
        eventId: result.usage_event_id,
        style: result.style,
      })
    } catch (caught) {
      const message =
        caught instanceof Error ? caught.message : 'The picture could not be generated.'
      setError(message)
      toast(message, 'error')
    } finally {
      setIsGenerating(false)
    }
  }

  const keepIt = async () => {
    if (!candidate) return
    try {
      const image = await api.acceptImage(candidate.eventId, candidate.prompt, candidate.dataUrl)
      await queryClient.invalidateQueries({ queryKey: ['pictures', 'all'] })
      reset()
      setOpen(false)
      onSaved?.()
      onUse?.(image.id)
    } catch (caught) {
      toast(
        caught instanceof Error ? caught.message : 'That picture could not be saved.',
        'error',
      )
    }
  }

  return (
    <div className="tc-gen">
      <div className="tc-gen-bar">
        <button
          type="button"
          className={`tc-gen-trigger${open ? ' is-open' : ''}`}
          aria-expanded={open}
          title="Describe a picture. We look through your school's library first, and only draw a new one if you ask - each new picture costs the school."
          onClick={() => {
            setOpen((was) => !was)
            if (open) reset()
          }}
        >
          <Sparkle />
          {open ? 'Close picture maker' : 'Make a picture with AI'}
        </button>
        {typeof status.quota_image_remaining === 'number' && (
          <span className="tc-gen-quota">{status.quota_image_remaining} left this month</span>
        )}
      </div>

      {open && (
        <div className="tc-gen-panel">
          <div className="tc-gen-row">
            <Input
              value={description}
              placeholder="What do you need? For example: a panda"
              aria-label="Describe the picture you need"
              onChange={(event) => {
                setDescription(event.target.value)
                setSearched(false)
                setCandidate(null)
              }}
              onKeyDown={(event) => event.key === 'Enter' && search()}
            />
            <Button variant="primary" disabled={!description.trim()} onClick={search}>
              Look in our pictures
            </Button>
          </div>

          {!searched && (
            <p className="tc-gen-hint">
              We check what your school already has before drawing anything new.
            </p>
          )}

          {searched && (
            <div className="tc-gen-results">
              {matches.length > 0 ? (
                <>
                  <p className="tc-gen-found">
                    <strong>{matches.length}</strong>{' '}
                    {matches.length === 1 ? 'picture' : 'pictures'} in your library already look
                    like this. Using one is instant and free.
                  </p>
                  <div className="tc-gen-matches">
                    {matches.map((image) => (
                      <button
                        key={image.id}
                        type="button"
                        className="tc-gen-match"
                        title={image.original_filename}
                        onClick={() => {
                          reset()
                          setOpen(false)
                          onUse?.(image.id)
                        }}
                      >
                        <Picture id={image.id} />
                        <span>{image.original_filename.replace(/\.[a-z0-9]+$/i, '')}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="tc-gen-found tc-gen-none">
                  Nothing in your library matches that yet.
                </p>
              )}

              <fieldset className="tc-gen-style">
                <legend>How should it be drawn?</legend>
                {STYLES.map((option) => (
                  <label key={option.value} className="tc-gen-style-option">
                    <input
                      type="radio"
                      name="picture-style"
                      value={option.value}
                      checked={style === option.value}
                      onChange={() => {
                        setStyle(option.value)
                        setCandidate(null)
                      }}
                    />
                    <span>
                      <strong>{option.label}</strong>
                      <em>{option.hint}</em>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="tc-gen-confirm">
                <p>
                  {matches.length > 0
                    ? 'If none of these will do, draw a new one:'
                    : 'Draw a new one:'}
                </p>
                <Button
                  variant="primary"
                  disabled={isGenerating}
                  onClick={generate}
                  icon={<Sparkle size={15} />}
                >
                  {isGenerating ? 'Drawing…' : 'Yes, draw a new picture'}
                </Button>
                {isGenerating && <Spinner label="Drawing" />}
              </div>
            </div>
          )}

          {isGenerating && (
            <p className="tc-gen-hint">Drawing can take a minute. This stays open while it works.</p>
          )}
          {error && (
            <p className="tc-gen-error" role="alert">
              {error}
            </p>
          )}

          {candidate && (
            <div className="tc-gen-candidate">
              <img src={candidate.dataUrl} alt={candidate.prompt} />
              <div className="tc-gen-candidate-side">
                <strong>Happy with this?</strong>
                {style === 'auto' && (
                  <p className="tc-gen-style-note">
                    Drawn {candidate.style === 'colour' ? 'in colour' : 'as black outlines'}.
                    Not what you wanted? Change it above and draw again.
                  </p>
                )}
                <p>
                  Keeping it adds it to your school's picture library, filed under the right
                  heading, so every teacher at your school can use it.
                </p>
                <div className="tc-gen-candidate-actions">
                  <Button variant="primary" onClick={keepIt}>
                    Keep it
                  </Button>
                  <Button variant="ghost" onClick={() => setCandidate(null)}>
                    Discard
                  </Button>
                  <Button variant="secondary" disabled={isGenerating} onClick={generate}>
                    Try again
                  </Button>
                </div>
              </div>
            </div>
          )}

          <p className="tc-gen-disclaimer">
            Pictures are shared. Anything kept here joins your school's library and is visible to
            every teacher at your school — it is not private to you. Each new picture costs the
            school, so please look through the library first.
          </p>
        </div>
      )}
    </div>
  )
}
